import { apiClient } from '@shared/utils/api-client';
import type { Order, OrderStatusHistory } from '../types';

export interface UpdateOrderStatusDto {
  status: string;
  changedBy: 'ADMIN';
  comment?: string;
}

export interface RefundOrderDto {
  amount?: number;
  cancellationComment?: string;
}

export interface RefundOrderResponse {
  success: boolean;
  paymentStatus: string;
  orderCanceled: boolean;
}

export const ordersApi = {
  /** Заказы в статусах PENDING и CONFIRMED по всем точкам продавца (для бейджа в кабинете). */
  getSellerPendingOrConfirmedCount: async (): Promise<{ count: number }> => {
    return apiClient.get<{ count: number }>('/orders/seller/pending-or-confirmed-count');
  },

  getHasOrdersOnOtherPickupPoints: async (
    excludePickupPointId: number,
  ): Promise<{ hasOrders: boolean }> => {
    return apiClient.get<{ hasOrders: boolean }>(
      `/orders/seller/has-orders-on-other-pickup-points?excludePickupPointId=${excludePickupPointId}`,
    );
  },

  /** Точки с заказами не «Доставлен» / не «Отменён» — кружки в селекте точки. */
  getPickupPointsWithActiveOrders: async (): Promise<{ pickupPointIds: number[] }> => {
    return apiClient.get<{ pickupPointIds: number[] }>(
      '/orders/seller/pickup-points-with-active-orders',
    );
  },

  getAll: async (): Promise<Order[]> => {
    return apiClient.get<Order[]>('/orders');
  },

  getById: async (id: number): Promise<Order> => {
    return apiClient.get<Order>(`/orders/${id}`);
  },

  getByPickupPointId: async (pickupPointId: number, status?: string | null): Promise<Order[]> => {
    const query = status != null && status !== '' ? `?status=${encodeURIComponent(status)}` : '';
    return apiClient.get<Order[]>(`/orders/pickup-point/${pickupPointId}${query}`);
  },

  /** Число заказов по статусам для точки (бейджи на табах). */
  getPickupPointStatusCounts: async (pickupPointId: number): Promise<Record<string, number>> => {
    return apiClient.get<Record<string, number>>(
      `/orders/pickup-point/${pickupPointId}/status-counts`,
    );
  },

  /** Пагинация для статусов DELIVERED/CANCELED: первые 50 по умолчанию, затем «загрузить ещё». Поиск по номеру/телефону/имени — на бэкенде. */
  getByPickupPointIdPage: async (
    pickupPointId: number,
    status: string,
    params: { limit?: number; offset?: number; search?: string },
  ): Promise<{ orders: Order[]; total: number }> => {
    const searchParams = new URLSearchParams();
    searchParams.set('status', status);
    if (params.limit != null) searchParams.set('limit', String(params.limit));
    if (params.offset != null) searchParams.set('offset', String(params.offset));
    if (params.search != null && params.search.trim() !== '') {
      searchParams.set('search', params.search.trim());
    }
    return apiClient.get<{ orders: Order[]; total: number }>(
      `/orders/pickup-point/${pickupPointId}?${searchParams.toString()}`,
    );
  },

  updateStatus: async (id: number, dto: UpdateOrderStatusDto): Promise<Order> => {
    return apiClient.put<Order>(`/orders/${id}/status`, dto);
  },

  updateReady: async (id: number, files: File[], comment?: string): Promise<Order> => {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('images', file);
    });
    if (comment) {
      formData.append('comment', comment);
    }
    formData.append('changedBy', 'ADMIN');

    // Используем axios напрямую для FormData
    const axiosInstance = (await import('@shared/utils/axios-instance')).default;
    const response = await axiosInstance.put<Order>(`/orders/${id}/ready`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  refund: async (id: number, dto?: RefundOrderDto): Promise<RefundOrderResponse> => {
    return apiClient.post<RefundOrderResponse>(`/orders/${id}/refund`, dto ?? {});
  },

  getStatusHistory: async (id: number): Promise<OrderStatusHistory[]> => {
    return apiClient.get<OrderStatusHistory[]>(`/orders/${id}/status-history`);
  },
};
